<script setup lang="ts">
import axios from 'axios';
import { reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GoogleAddressAutocomplete from 'vue3-google-address-autocomplete';
import FormData from 'form-data';

import { API_URL, GOOGLE_MAPS_API_KEY } from '@/common/constantes.ts';
import apiClient from '@/common/api-client.ts';

type EmpresaForm = {
  tin: string;
  nombre: string;
  descripcion: string;
  direccion: string;
  ciudad: string;
  tipo: string;
  subtipos: string; // comma separated
  telefono: string;
  email: string;
  sitioWeb: string;
  horarioInicio: string;
  horarioFin: string;
  dias: string; // comma separated
  latitud?: string;
  longitud?: string;
};

const route = useRoute();
const $router = useRouter();
try {
  await apiClient.get('/profile');

  if (!route.query.idEmpresa) {
    $router.push('/');
  }
} catch (e: any) {
  if (e.status === 400) {
    $router.push('/login');
  }
}

const apiBaseUrl = API_URL || '/api';

const form = reactive<EmpresaForm>({
  tin: '',
  nombre: '',
  descripcion: '',
  direccion: '',
  ciudad: '',
  tipo: 'Restaurante',
  subtipos: '',
  telefono: '',
  email: '',
  sitioWeb: '',
  horarioInicio: '12:00',
  horarioFin: '16:00',
  dias: 'Lunes,Martes,Miercoles,Jueves,Viernes',
  latitud: '',
  longitud: '',
});

async function submit() {
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const payload = {
      id: route.query.idEmpresa,
      tin: form.tin,
      nombre: form.nombre,
      direccion: form.direccion,
      ciudad: form.ciudad,
      tipo: form.tipo,
      subtipos: form.subtipos
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
      telefono: form.telefono,
      email: form.email,
      sitioWeb: form.sitioWeb,
      horario: { inicio: form.horarioInicio as '12:00', fin: form.horarioFin as '16:00' },
      dias: form.dias
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
      descripcion: form.descripcion,
      activa: true,
      publicada: false,
      latitud: form.latitud !== '' ? Number(form.latitud) : undefined,
      longitud: form.longitud !== '' ? Number(form.longitud) : undefined,
    } as any;
    await axios.put(`${apiBaseUrl}/empresa`, payload, { headers });
    await uploadFile();
    alert('Restaurante registrado correctamente');
    $router.push('/eventos/lista');
  } catch (e: any) {
    if (e?.response?.status === 401) {
      location.href = `${apiBaseUrl}/auth/login`;
      return;
    }
    alert(e?.response?.data?.message || 'No se pudo registrar el restaurante');
  }
}

function asignarCoordenadas(place: any) {
  form.latitud = place.geometry?.location?.lat?.();
  form.longitud = place.geometry?.location?.lng?.();
}
const apiUrl = import.meta.env.VITE_API_URL;
const file = ref('');

async function handleFileUpload(e: any) {
  file.value = e.target.files[0];
}

async function uploadFile() {
  if (!file.value) return;
  const formData = new FormData();
  formData.append('foto', file.value);
  await axios.post(`${apiUrl}/empresa/foto`, formData);
}
</script>

<template>
  <section class="mx-auto max-w-2xl px-4 py-8">
    <router-link to="/eventos/lista" class="mb-6 inline-block text-teal-600 hover:underline"
      >← Volver</router-link
    >
    <h1 class="mb-4 text-2xl font-bold text-gray-900">Registrar Restaurante</h1>
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
          <input v-model="form.nombre" class="w-full rounded border px-3 py-2" required />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Ciudad</label>
          <input v-model="form.ciudad" class="w-full rounded border px-3 py-2" required />
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">RUT</label>
          <input v-model="form.tin" class="w-full rounded border px-3 py-2" required />
        </div>
      </div>

      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
        <textarea v-model="form.descripcion" class="w-full rounded border px-3 py-2" required />

        <label class="mb-1 block text-sm font-medium text-gray-700">Imagen</label>
        <input data-testid="upload" type="file" ref="fileInput" @change="handleFileUpload" />
      </div>

      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Dirección</label>
        <google-address-autocomplete
          :apiKey="GOOGLE_MAPS_API_KEY || 'NO_API_KEY_PROVIDED'"
          v-model="form.direccion"
          class="w-full rounded border px-3 py-2"
          placeholder="Calle 123, Ciudad"
          @callback="asignarCoordenadas"
        />
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Latitud</label>
          <input
            v-model="form.latitud"
            inputmode="decimal"
            class="w-full rounded border px-3 py-2"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Longitud</label>
          <input
            v-model="form.longitud"
            inputmode="decimal"
            class="w-full rounded border px-3 py-2"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
          <input v-model="form.tipo" class="w-full rounded border px-3 py-2" />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Subtipos (coma)</label>
          <input
            v-model="form.subtipos"
            class="w-full rounded border px-3 py-2"
            placeholder="Sushi,Vegetariano"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
          <input v-model="form.telefono" class="w-full rounded border px-3 py-2" />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Email</label>
          <input type="email" v-model="form.email" class="w-full rounded border px-3 py-2" />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Sitio Web</label>
          <input
            v-model="form.sitioWeb"
            class="w-full rounded border px-3 py-2"
            placeholder="https://"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Horario inicio</label>
          <input type="time" v-model="form.horarioInicio" class="w-full rounded border px-3 py-2" />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Horario fin</label>
          <input type="time" v-model="form.horarioFin" class="w-full rounded border px-3 py-2" />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Días (coma)</label>
          <input
            v-model="form.dias"
            class="w-full rounded border px-3 py-2"
            placeholder="Lunes,Martes,..."
          />
        </div>
      </div>

      <button type="submit" class="rounded bg-teal-600 px-4 py-2 text-white hover:bg-teal-700">
        Registrar
      </button>
    </form>
  </section>
</template>

<style scoped></style>
